/**
 * @description Module file for Demand Days Picker component
 * Handles API calls to fetch demand days data
 */

import { postCall } from '../../utils/network.util';
import { GET_DEMAND_DAYS_URL } from '../../utils/api-url';

/**
 * Fetch demand days data for a specific month
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string|Array} origin - Origin city or array of origins (defaults to 'all')
 * @param {string|Array} destination - Destination city or array of destinations (defaults to 'all')
 * @returns {Promise} - Promise with demand days data
 */
export const fetchDemandDays = async (startDate, endDate, origin = 'all', destination = 'all') => {
    try {
        // Convert origin and destination to arrays and always include 'all'
        let originList = Array.isArray(origin) ? origin : [origin];
        let destinationList = Array.isArray(destination) ? destination : [destination];
        
        // If a specific city is selected, include both the city and 'all'
        if (origin && origin !== 'all' && !originList.includes('all')) {
            originList = [origin, 'all'];
        } else if (!origin || origin === 'all') {
            originList = ['all'];
        }
        
        if (destination && destination !== 'all' && !destinationList.includes('all')) {
            destinationList = [destination, 'all'];
        } else if (!destination || destination === 'all') {
            destinationList = ['all'];
        }

        const payload = {
            originList: originList,
            destinationList: destinationList,
            startDate: startDate,
            endDate: endDate
        };

        const response = await postCall(GET_DEMAND_DAYS_URL, payload);
        
        if (response.status === 'SUCCESS' && response.payload.Status === 'SUCCESS') {
            return {
                success: true,
                data: response.payload.Payload || []
            };
        } else {
            return {
                success: false,
                data: [],
                message: response.payload.Message || 'Failed to fetch demand days'
            };
        }
    } catch (error) {
        console.error('Error fetching demand days:', error);
        return {
            success: false,
            data: [],
            message: 'Error fetching demand days'
        };
    }
};

/**
 * Format demand days data for calendar display
 * @param {Array} demandDaysData - Raw demand days data from API
 * @returns {Object} - Formatted data with dates as keys
 */
export const formatDemandDaysForCalendar = (demandDaysData) => {
    const formattedData = {};
    
    if (!demandDaysData || !Array.isArray(demandDaysData)) {
        return formattedData;
    }

    demandDaysData.forEach(item => {
        let date = item.demand_date;
        if (date) {
            // Normalize the date format to YYYY-MM-DD (remove time if present)
            // Handle both "2025-12-25" and "2025-12-25T00:00.000Z" formats
            if (date.includes('T')) {
                date = date.split('T')[0];
            }
            
            if (!formattedData[date]) {
                formattedData[date] = [];
            }
            formattedData[date].push({
                description: item.description || 'Peak/Festival Day',
                originCity: item.origin_city,
                destinationCity: item.destination_city
            });
        }
    });

    return formattedData;
};

